import { useEffect, useMemo, useState, useRef } from 'react';
import {
  GridRowModes,
  useGridApiRef,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid-premium';
import { Box, Button, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import { sxConfig } from './OrderExecutionStyle';
import { dataGridPremiumColumns, generateAutocompleteItems, getContrastColor } from '../../utils/helper';
import {
  DataGridIdentifiers,
  InputUI,
  DatagridPremiumUi,
  DatePickerUI,
  AutoCompleteSelectUI,
  ConfirmDialogUI,
  SelectUI,
  DrillArrow,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';
import orderExecutionService from '../../service/orderExecution.service';
import documentTypeService from '../../service/newDocumentType.service';
import masterDataService from '../../service/masterData.service';
import SourceListDialog from '../SourceList/SourceListDialog';

import ProductionDialog from '../Production/NewProduction/ProductionDialog';
import { useLoader } from '../../provider/LoaderProvider';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { formatCurrency } from '../../utils/currencyHelper';
import { StockArticleType } from '../../config/itemGroup';
import { DocumentType } from '../../config/DocumentType';
import { options } from '@fullcalendar/core/preact.js';

const OrderExecution = () => {
  const apiRef = useGridApiRef();
  const { showLoader, hideLoader } = useLoader();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [updData, setUpdData] = useState({});
  const [selectedItemCode, setSelectedItemCode] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState({});
  const [lockedRowIds, setLockedRowIds] = useState([]); // rows locked during async updates
  const [customDocStatuses, setCustomDocStatuses] = useState([]);
  const [customDocStatusOptions, setCustomDocStatusOptions] = useState([]);

  // Debounce helpers
  const debounceTimersRef = useRef({});
  const scheduleDebounced = (key, fn, delay = 600) => {
    const timers = debounceTimersRef.current;
    if (timers[key]) clearTimeout(timers[key]);
    timers[key] = setTimeout(() => {
      delete timers[key];
      fn();
    }, delay);
  };

  // helper to lock / unlock rows
  const lockRow = (rowIds) => {
    if (rowIds == null || !rowIds.length) return;

    setLockedRowIds((prev) => [...new Set([...prev, ...rowIds])]);
  };

  const unlockRow = (rowIds) => {
    if (rowIds == null || !rowIds.length) return;
    setLockedRowIds((prev) => prev.filter((id) => !rowIds.includes(id)));
  };

  const getList = () => {
    const gridState = {
      filter: { filterModel: { items: [], logicOperator: 'and' } },
      sorting: { sortModel: [] },
      pagination: {},
    };
    return orderExecutionService.getOrders(gridState, null).then((res) => {
      setReports(res);
      return res;
    });
  };

  const getCustomDocStatuses = async () => {
    const docType = await documentTypeService.getDocumentType(DocumentType.CustomerOrder);

    const customStatusItems = Array.isArray(docType.customDocStatus) ? docType.customDocStatus : [];
    const activeCustomStatusItems = customStatusItems.filter((item) => item.active);

    const ct = generateAutocompleteItems(activeCustomStatusItems, 'name', 'code');

    setCustomDocStatuses(activeCustomStatusItems);
    setCustomDocStatusOptions(ct);
  };

  const getData = () => {
    showLoader();
    return getList()
      .then((res) => {
        return Promise.all([getCustomDocStatuses()]);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
    getData();
  }, []);

  // Clear any pending debounced tasks on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          users: resp[0],
        };
        setAutocompleteData(aData);

        const technologists = aData.users.filter((user) => user.technologist);

        const acOptions = {
          technologists: generateAutocompleteItems(technologists, 'nickName', 'userId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'dueDate', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  const handleFieldChange = async (fieldName, newValue, row, allLine = false) => {
    const docId = row?.doc?.docId;

    const findedLines = allLine ? reports.rows.filter((r) => r.doc?.docId === docId) : [row];
    const rowIds = findedLines.map((r) => r.id);

    try {
      lockRow(rowIds);

      const value = newValue === '' ? null : newValue;
      const requestBody = {
        ...row,
        allLine,
        [fieldName]: value,
      };

      await orderExecutionService.updateOrderLine(requestBody);

      const updatedLines = findedLines.map((r) => ({ ...r, [fieldName]: value }));

      setReports((prev) => {
        const map = new Map(prev.rows.map((r) => [r.id, r]));
        updatedLines.forEach((r) => {
          map.set(r.id, { ...map.get(r.id), ...r });
        });
        return {
          ...prev,
          rows: [...map.values()],
        };
      });
    } catch (error) {
      console.error('Error updating field:', error);
      getList();
    } finally {
      unlockRow(rowIds);
    }
  };

  const handleDocFieldChange = async (fieldNameOrUpdates, newValue, row) => {
    const docId = row?.doc?.docId;
    const findedLines = reports.rows.filter((r) => r.doc?.docId === docId);
    const rowIds = findedLines.map((r) => r.id);
    const fieldUpdates =
      typeof fieldNameOrUpdates === 'string'
        ? { [fieldNameOrUpdates]: newValue === '' ? null : newValue }
        : Object.fromEntries(
            Object.entries(fieldNameOrUpdates ?? {}).map(([field, value]) => [field, value === '' ? null : value])
          );

    try {
      lockRow(rowIds);

      const requestBody = {
        ...(row?.doc || {}),
        ...fieldUpdates,
      };

      await orderExecutionService.updateOrderHead(requestBody);

      const updatedLines = findedLines.map((r) => ({
        ...r,
        doc: {
          ...(r.doc || {}),
          ...fieldUpdates,
        },
      }));

      setReports((prev) => {
        const map = new Map(prev.rows.map((r) => [r.id, r]));
        updatedLines.forEach((r) => {
          map.set(r.id, { ...map.get(r.id), ...r });
        });
        return {
          ...prev,
          rows: [...map.values()],
        };
      });
    } catch (error) {
      console.error('Error updating doc field:', error);
      getList();
    } finally {
      unlockRow(rowIds);
    }
  };

  const resolveDocCustomStatus = (row) => {
    const doc = row?.doc ?? null;
    if (!doc) return null;

    if (doc?.customDocStatus) return doc.customDocStatus;
    if (doc?.customStatusData) return doc.customStatusData;

    const code = doc?.customStatus ?? null;
    if (!code) return null;

    const docTypeId = doc?.documentTypeId ?? doc?.documentType?.documentTypeId ?? null;

    return (
      customDocStatuses.find(
        (status) =>
          status.code === code && (!docTypeId || status?.documentTypeId == null || status?.documentTypeId === docTypeId)
      ) ?? null
    );
  };

  const getDocCustomStatusOptions = (row) => {
    const docTypeId = row?.doc?.documentTypeId ?? row?.doc?.documentType?.documentTypeId ?? null;

    return (customDocStatuses ?? [])
      .filter((s) => {
        if (s?.active === false) return false;
        if (!docTypeId) return true;
        return s?.documentTypeId == null || s?.documentTypeId === docTypeId;
      })
      .map((s) => ({ value: s.code, label: s.name }));
  };

  const columns = useMemo(() => {
    const roleId = user.roleId;
    const ppCols = JSON.parse(params?.ORDER_EXECUTION_COLS ?? '{}');

    const allColumns = [
      {
        field: 'doc_docNum',
        headerName: 'Rendelés szám',
        type: 'string',
        valueGetter: (_val, row) => row.doc?.docNum ?? '',
        renderCell: (data) => {
          const status = resolveDocCustomStatus(data.row);
          const statusColor = status?.color ?? null;
          const statusTextColor = statusColor ? getContrastColor(statusColor) : undefined;

          return (
            <Box
              sx={
                statusColor
                  ? {
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      backgroundColor: `${statusColor} !important`,
                      color: `${statusTextColor} !important`,
                      px: 1,
                      '& svg': {
                        color: `${statusTextColor} !important`,
                      },
                    }
                  : {
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 1,
                    }
              }
            >
              <Box component="span">{data.row.doc?.docNum}</Box>
              <DrillArrow
                onClick={(e) => {
                  productionDialogOpen(data.row.doc?.docId, data.row.lineNum);
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'doc_customStatus',
        headerName: 'Egyéni státusz',
        type: 'singleSelect',
        valueOptions: customDocStatusOptions,
        sortable: false,
        valueGetter: (_val, row) => row?.doc?.customStatus ?? '',
        renderCell: (data) => resolveDocCustomStatus(data.row)?.name ?? '',
        renderEditCell: (data) => {
          const currentValue = data.value ?? '';

          return (
            <SelectUI
              value={currentValue}
              option={customDocStatusOptions}
              onChange={(e) => {
                const newValue = e.target.value ?? '';

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                handleDocFieldChange('customStatus', newValue, data.row);
              }}
            />
          );
        },
      },
      {
        field: 'itemReference',
        headerName: 'Hivatkozás',
        type: 'string',
        valueGetter: (_val, row) => {
          const docReference = row.doc?.reference;
          const itemReference = row.itemReference;

          return docReference && itemReference
            ? `${docReference} - ${itemReference}`
            : docReference || itemReference || '';
        },
      },
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.dueDate ? new Date(row.dueDate) : null;
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY.MM.DD');
        },
      },
      {
        field: 'doc_companyCode',
        headerName: 'Üp. kód',
        type: 'string',
        sortable: false,
        valueGetter: (_val, row) => row.doc?.companyCode ?? '',
      },
      {
        field: 'doc_companyName',
        headerName: 'Üp. név',
        type: 'string',
        sortable: false,
        valueGetter: (_val, row) => row.doc?.companyName ?? '',
      },
      {
        field: 'itemGroup',
        headerName: 'Cikkcsoport',
        type: 'string',
        valueGetter: (_val, row) => row.item?.itemGroup?.name ?? '',
      },
      {
        field: 'itemCode',
        headerName: 'Cikkszám',
        type: 'string',

        renderCell: (params) => {
          return (
            <div
              className={
                params.row.missingtree === 1 || params.row.missingOperation === 1
                  ? 'datagrid-line-missingTree'
                  : params.row.missingSourceList
                    ? 'datagrid-line-missingSourceList'
                    : params.row.isMaterial || params.row.item?.stockItemType === StockArticleType.COMMERCIALPRODUCT
                      ? 'datagrid-line-isMaterial'
                      : ''
              }
            >
              {params.row.itemCode}

              <DrillArrow
                onClick={(e) => {
                  sourceListDialogOpen(params.row.itemCode);
                }}
              />
            </div>
          );
        },
      },

      {
        field: 'drawingNumber',
        headerName: 'Rajzszám',
        type: 'string',
        editable: true,
        valueGetter: (_val, row) => row?.drawingNumber ?? '',
        renderEditCell: (data) => {
          return (
            <InputUI
              name="drawingNumber"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.drawingNumber ?? ''}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                scheduleDebounced(
                  `drawingNumber-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },

      {
        field: 'itemName',
        headerName: 'Cikknév',
        type: 'string',
      },

      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
      {
        field: 'unit',
        headerName: 'Mértékegység',
        type: 'string',
        valueGetter: (_val, row) => row.item?.quantityUnit.code ?? '',
      },
      {
        field: 'qntStatus',
        headerName: 'Tétel állapot',
        type: 'string',
      },

      {
        field: 'respPersonId',
        headerName: 'Felelős',
        type: 'singleSelect',
        sortable: false,
        valueGetter: (_data, row) => {
          return row?.respPersonId;
        },
        editable: true,
        valueOptions: autoCompleteOptions.technologists,
        renderEditCell: (data) => {
          const findedopts =
            autoCompleteOptions.technologists.find((opt) => opt.value === data.row?.respPersonId) ?? null;

          return (
            <AutoCompleteSelectUI
              name="respPersonId"
              variant="standard"
              onChange={(_e, newVal) => {
                const newValue = newVal?.value ?? null;
                setShowConfirmDialog(true);
                setUpdData({ field: data.field, row: data.row, value: newValue });
                //handleFieldChange('respPersonId', newValue, data.row);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });
              }}
              value={findedopts}
              dataset={autoCompleteOptions.technologists}
            />
          );
        },
      },
      {
        field: 'respDeadLine',
        headerName: 'Felelős határidő',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row?.respDeadLine ? new Date(row?.respDeadLine) : null;
        },
        renderCell: (data) => {
          return data.row?.respDeadLine ? dayjs(data.row?.respDeadLine).format('YYYY.MM.DD') : '';
        },
        renderEditCell: (data) => {
          return (
            <DatePickerUI
              name="respDeadLine"
              type="date"
              variant="standard"
              fullWidth
              value={dayjs(data.row?.respDeadLine)}
              onChange={async (e) => {
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                setShowConfirmDialog(true);
                setUpdData({ field: data.field, row: data.row, value: newValue });
                //handleFieldChange('respDeadLine', newValue, data.row);
                await apiRef.current.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
            />
          );
        },
      },
      {
        field: 'respStatus',
        headerName: 'Felelős státusz',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="respStatus"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.respStatus}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                // Update UI immediately
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                // Debounce the server update (per row+field key)
                scheduleDebounced(
                  `respStatus-${data.id}`,
                  () => {
                    setShowConfirmDialog(true);
                    setUpdData({ field: data.field, row: data.row, value: newValue });
                    //handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },
      {
        field: 'internalNote',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="internalNote"
              type="text"
              variant="standard"
              fullWidth
              value={data.row.internalNote}
              onChange={(e) => {
                const newValue = e.target.value ?? null;

                // Update UI immediately
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newValue,
                });

                // Debounce the server update (per row+field key)
                scheduleDebounced(
                  `internalNote-${data.id}`,
                  () => {
                    handleFieldChange(data.field, newValue, data.row);
                  },
                  600
                );
              }}
            />
          );
        },
      },
      {
        field: 'onHand',
        headerName: 'Készleten',
        type: 'number',
        sortable: false,
      },

      {
        field: 'doc_docDate',
        headerName: 'Rendelés dátuma',
        type: 'number',
        valueGetter: (_data, row) => {
          return row.doc?.docDate ? new Date(row.doc.docDate) : null;
        },
        renderCell: (data) => {
          return dayjs(data.row.doc?.docDate).format('YYYY.MM.DD');
        },
      },
      {
        field: 'doc_internalNote',
        headerName: 'Belső megjegyzés',
        type: 'string',
        valueGetter: (_val, row) => row.doc?.internalNote ?? '',
      },

      {
        field: 'doc_comment',
        headerName: 'Rendelés megjegyzés',
        type: 'string',
        valueGetter: (_val, row) => row.doc?.comment ?? '',
      },

      {
        field: 'bpNote',
        headerName: 'ÜP megjegyzés',
        type: 'string',
      },
      {
        field: 'doc_salesOrPurchasePersonId',
        headerName: 'Értékesítő',
        valueGetter: (_data, row) => {
          return row.doc?.salesOrPurchasePerson?.name ?? '';
        },
      },
      {
        field: 'opnQty',
        headerName: 'Nyitott mennyiség',
        type: 'number',
      },
      {
        field: 'unitPrice',
        headerName: 'Egységár',
        type: 'number',
        valueGetter: (value) => parseFloat(value),
        valueFormatter: (params) => {
          return params ? formatCurrency(params) : '';
        },
      },
      {
        field: 'currency',
        headerName: 'Pénznem',
        type: 'string',
        valueGetter: (_val, row) => row.doc?.currency?.code ?? '',
      },
      {
        field: 'netPrice',
        headerName: 'Nettó érték',
        type: 'number',
        editable: false,
        sortable: false,
        valueGetter: (value, row) => {
          return parseFloat(value);
        },
        valueFormatter: (params) => {
          return params ? formatCurrency(params) : '';
        },
      },
    ];

    const filteredColumns = allColumns
      .filter((col) => {
        const perms = ppCols?.[col.field];
        if (!perms) return false;
        return perms.read.includes(roleId) || perms.edit?.includes(roleId);
      })
      .map((col) => {
        const perms = ppCols?.[col.field];
        const editable = perms?.edit?.includes(roleId);
        return { ...col, editable };
      });
    return dataGridPremiumColumns(filteredColumns.length > 0 ? filteredColumns : allColumns);
  }, [autoCompleteOptions, customDocStatuses, params, user.roleId, reports]);

  const getRowClassName = (params) => {
    let classNames = '';

    if (lockedRowIds.includes(params.id)) classNames += 'datagrid-line-locked ';

    return classNames.trim();
  };

  const sourceListDialogOpen = (itemCode) => {
    setSelectedItemCode(itemCode);
  };

  const productionDialogOpen = (docId, lineNum) => {
    setSelectedOrder({ selectedOrder: docId, selectedLineNum: lineNum });
  };

  const sourceListDialogClose = () => {
    setSelectedItemCode(null);
  };

  const handleRefresh = () => {
    getData();
  };

  const getUniqueEditIds = () => {
    const permissionsObj = JSON.parse(params?.VM_PRODPLANNER_COLS ?? '{}');
    return [...new Set(Object.values(permissionsObj).flatMap((entry) => entry.edit ?? []))].sort((a, b) => a - b);
  };

  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          px: 1,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Frissítés">
          <Button
            size="small"
            sx={{ ml: 'auto' }}
            startIcon={<RefreshIcon />}
            onClick={() => {
              handleRefresh();
            }}
          >
            Frissítés
          </Button>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Box sx={sxConfig.containerBox}>
        <Box sx={sxConfig.formBox}>
          <DatagridPremiumUi
            apiRef={apiRef}
            datagridIdentifier={DataGridIdentifiers.OrderExecution}
            datagridInitialState={initialState}
            height={window.innerHeight - 100}
            columns={columns}
            rows={reports.rows}
            editMode="row"
            paginationAndfilterMode="client"
            onCellDoubleClick={(_params, event) => {
              event.defaultMuiPrevented = true;
            }}
            onCellClick={(gridParams, event) => {
              const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

              // do nothing if this row is currently locked
              if (lockedRowIds.includes(gridParams.id)) {
                return addNotification({
                  content: 'A sor zárolva van, mert szerkesztés alatt áll. Próbáld később.',
                  type: NotificationType.WARNING,
                });
              }

              const uniqueEditIds = getUniqueEditIds();
              const hasEdit = uniqueEditIds.includes(user?.roleId ?? -1);

              if (!hasEdit) return;

              for (const rowId of rowsInEditMode) {
                if (gridParams.row.id !== rowId) {
                  apiRef.current.stopRowEditMode({ id: rowId });
                }
              }

              const rowMode = apiRef.current.getRowMode(gridParams.row.id);
              if (rowMode === GridRowModes.View) {
                apiRef.current.startRowEditMode({ id: gridParams.row.id });
              }
            }}
            getRowHeight={() => 'auto'}
            getRowClassName={getRowClassName}
            gridExport={true}
            slots={{ toolbar: CustomToolbar }}
          />
        </Box>
      </Box>
      <ConfirmDialogUI
        handleChangeElement={() => {
          handleFieldChange(updData.field, updData.value, updData.row, true);
          setShowConfirmDialog(false);
        }}
        show={showConfirmDialog}
        onHide={() => {
          handleFieldChange(updData.field, updData.value, updData.row);
          setShowConfirmDialog(false);
        }}
        message={'Frissíti a rendeléshez tartozó összes tételt?'}
      />

      <SourceListDialog
        showDialog={selectedItemCode}
        onClose={sourceListDialogClose}
        selectedItemCode={selectedItemCode}
      />

      <ProductionDialog
        showDialog={selectedOrder.selectedOrder}
        onClose={() => {
          setSelectedOrder({});
        }}
        selectedOrderData={selectedOrder}
      />
    </>
  );
};

export default OrderExecution;
